import { contentStyles } from "../../content";
import { useCurrentSite } from "../../contexts/models/SiteContext";
import Track from "../../core/instrumentation";
import EventMap from "../../map/Map";
import MapContainer from "../../map/MapContainer";
import PageComponent from "../../pages/website/Page";
import { Configuration, Page, WebsiteEvent } from "../../resources/modelTypes";
import FancyClothesContext from "../../website/components/FancyClothesContext";
import { focusLocation } from "../actions";
import { formatAddress } from "../utils";
import Event from "./Event";
import { css } from "@emotion/react";
import { Button } from "@minted/fancyclothes";
import config from "config";
import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

const googleMapsApiKey = config.get("googleMapsApiKey");

const buttonContainerStyles = css`
  display: flex;
  justify-content: center;
`;

export type EventProps = {
  configuration: Configuration;
  dispatch?: any;
  events: WebsiteEvent[];
  id: string;
  pageData: Page;
  removeSpacing: boolean;
  title: string;
};

type EventPropsWithRedux = {
  focusedLocationIndex: number;
} & EventProps;

const Events = ({
  configuration,
  dispatch,
  events,
  focusedLocationIndex,
  id,
  pageData,
  removeSpacing,
  title,
}: EventPropsWithRedux) => {
  const site = useCurrentSite();
  const { isWedding } = site;
  const [hasValidLocations, setHasValidLocations] = useState(false);

  const [isMapExpanded, setIsMapExtended] = useState(false);

  const [showMap, setShowMap] = useState(false);

  const onExpandToggle = useCallback(() => {
    setIsMapExtended((prevValue) => !prevValue);
  }, []);

  const onShowToggle = useCallback(() => {
    Track.emit({
      event: "map_button_click",
    });

    setShowMap(true);
  }, []);

  const onLocationsGeocoded = useCallback((hasValidLocations) => {
    setHasValidLocations(hasValidLocations);
  }, []);

  const locations: (WebsiteEvent & { formattedAddress: string })[] = useMemo(
    () =>
      events.map((event: WebsiteEvent) => ({
        ...event,
        formattedAddress: event.hasPhysicalAddress ? formatAddress(event) : "",
      })),
    [events],
  );

  const hasAddresses = locations.some((location) =>
    Boolean(location.formattedAddress),
  );

  return (
    <FancyClothesContext.Consumer>
      {() => (
        <PageComponent
          id={id}
          pageData={pageData}
          removeSpacing={removeSpacing}
          title={title}
        >
          <div css={isMapExpanded && contentStyles.hideMd}>
            {locations.map((item, index) => (
              <Event
                configuration={configuration}
                event={item}
                index={index}
                key={index}
                showListNumber={configuration.showMap && hasValidLocations}
              />
            ))}
          </div>
          {isWedding &&
            (configuration.showMap && hasAddresses ? (
              showMap ? (
                <MapContainer
                  isExpanded={isMapExpanded}
                  onExpandToggle={onExpandToggle}
                >
                  <EventMap
                    apiKey={googleMapsApiKey}
                    focusedLocationIndex={focusedLocationIndex}
                    focusLocation={(index) => dispatch(focusLocation(index))}
                    locations={locations}
                    onLocationsGeocoded={onLocationsGeocoded}
                    site={site}
                  />
                </MapContainer>
              ) : (
                <div css={buttonContainerStyles}>
                  <Button onClick={onShowToggle} text="Show Map" />
                </div>
              )
            ) : null)}
        </PageComponent>
      )}
    </FancyClothesContext.Consumer>
  );
};

const mapStateToProps = (state) => {
  const { focusedLocationIndex } = state.events;

  return {
    focusedLocationIndex,
  };
};

export default connect(mapStateToProps)(Events) as React.FC<EventProps>;
